import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { App, Badge, Button, Col, DatePicker, Input, Row, Select, Spin, Table, Tag, Tooltip } from 'antd';
import type { Dayjs } from 'dayjs';
import { debounce } from 'lodash';
import moment from 'moment';
import queryString from 'query-string';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  clearSelectedRecords,
  setSelectedRecords,
  updateProps,
} from '../../../contextRedux/InterventionFileProject/InterventionFileProjectModel';
import { setInterventionProjectFiles } from '../../../contextRedux/InterventionFileProject/InterventionFileProjectStore';
import CompanyService from '../../../services/company/CompanyService';
import InterventionFileProjectService from '../../../services/interventionFileProject/InterventionFileProjectService';
import SystemTypeService from '../../../services/systemType/SystemTypeService';
import { RootState } from '../../../store';
import { usePermission } from '../../../utils/usePermission';
import InterventionFileProjectInformation from '../InterventionFileProjectInformation/InterventionFileProjectInformation';
import './InterventionFileProjectTable.css';

interface InterventionFileProjectTableProps {}

const InterventionFileProjectTable: FC<InterventionFileProjectTableProps> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const searchTerm = useSelector((state: RootState) => state.models?.interventionProjectFile.props.searchTerm);
  const searchTermType = useSelector((state: RootState) => state.models?.interventionProjectFile.props.searchTermType);
  const skip = useSelector((state: RootState) => state.models?.interventionProjectFile.props.skip);
  const take = useSelector((state: RootState) => state.models?.interventionProjectFile.props.take);
  const refresh = useSelector((state: RootState) => state.models?.interventionProjectFile.props.refresh);

  const reduxInterventionFileProject = useSelector((state: RootState) => state.models?.interventionProjectFile);
  const interventionFiles = useSelector((state: RootState) => state.store?.interventionFileProject.data ?? []);
  const selectedRecords = useSelector(
    (state: RootState) => state.models?.interventionProjectFile.selectedRecords ?? [],
  );
  const previousParamsRef = useRef({ term: '', page: '', pageSize: '' });

  const currentPage = Math.floor(reduxInterventionFileProject.props.skip / reduxInterventionFileProject.props.take) + 1;
  const pageSizes = [10, 20, 30, 50];

  const [isSearchTermInitialized, setIsSearchTermInitialized] = useState<boolean>(false);
  const [modalInformation, setModalInformation] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);

  // Add new state variables for filters
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [selectedWorkPlace, setSelectedWorkPlace] = useState<any>(null);
  const [selectedSystemType, setSelectedSystemType] = useState<any>(null);

  // Add new state for date filter
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);

  const hasAdminPermission = usePermission(['admin', 'manager', 'dispatcher']);
  const { message } = App.useApp();
  //const { profile } = useAuth();

  useEffect(() => {
    const queryParams = queryString.parse(location.search);

    const termInUrl = (queryParams.term as string) || '';
    // Convert page/pageSize from URL to skip/take
    const pageInUrl = Number(queryParams.page) || 1;
    const pageSizeInUrl = Number(queryParams.pageSize) || take; // fallback to default take if not provided
    // Calculate skip based on pageInUrl and pageSizeInUrl
    const calculatedSkip = (pageInUrl - 1) * pageSizeInUrl;
    // Dispatch updates to redux store
    dispatch(updateProps({ field: 'searchTerm', value: termInUrl }));
    dispatch(updateProps({ field: 'skip', value: calculatedSkip }));
    dispatch(updateProps({ field: 'take', value: pageSizeInUrl }));

    // Store these initial values in a ref to avoid extra triggers
    previousParamsRef.current = { term: termInUrl, page: pageInUrl.toString(), pageSize: pageSizeInUrl.toString() };
    setIsSearchTermInitialized(true);
  }, [dispatch, location.search, take]);

  useEffect(() => {
    if (isSearchTermInitialized) {
      const queryParams = queryString.parse(location.search);
      // Derive page number from skip/take
      const currentPage = Math.floor(skip / take) + 1;
      // Only update URL if something changed
      if (
        searchTerm !== previousParamsRef.current.term ||
        currentPage.toString() !== previousParamsRef.current.page ||
        take.toString() !== previousParamsRef.current.pageSize
      ) {
        const params = {
          ...queryParams,
          term: searchTerm || undefined,
          page: currentPage > 1 ? currentPage.toString() : undefined,
          pageSize: take !== 10 ? take.toString() : undefined, // only set if different from default
        };
        const newSearch = queryString.stringify(params);
        navigate(`${location.pathname}?${newSearch}`, { replace: true });
        // Update the ref to the new state
        previousParamsRef.current = {
          term: searchTerm || '',
          page: currentPage.toString(),
          pageSize: take.toString(),
        };
      }
    }
  }, [searchTerm, skip, take, isSearchTermInitialized, navigate, location.pathname, location.search]);

  // Update the query to include new filters
  const {
    data: _interventionFiles,
    refetch,
    isFetching: isInterventionFilesLoading,
  } = useQuery({
    queryKey: [
      'interventionFileProjects',
      searchTerm,
      searchTermType,
      skip,
      take,
      refresh,
      selectedCompany,
      selectedWorkPlace,
      selectedSystemType,
      selectedDate,
    ],
    queryFn: () =>
      InterventionFileProjectService.search({
        isInterventionFileSystemTypeIncluded: true,
        isInterventionFileEngineerIncluded: true,
        isInterventionFilePerformOperationsIncluded: true,
        isInterventionFileEquipmentsIncluded: true,
        isMentenancePlanningIncluded: true,
        isCompanyIncluded: true,
        isWorkPlaceIncluded: true,
        isAgentIncluded: true,
        isRequestFileIncluded: true,
        termen: searchTerm || null,
        termenType: searchTermType || null,
        companyName: selectedCompany || undefined,
        workPlaceName: selectedWorkPlace || undefined,
        systemTypeName: selectedSystemType || undefined,
        deliveryDate: selectedDate?.format('YYYY-MM-DD') || undefined,
        skip,
        take,
      }).then((data: any) => {
        dispatch(updateProps({ field: 'totalItems', value: data?.totalItems || 0 }));
        dispatch(setInterventionProjectFiles(data?.response));
        return data?.response;
      }),
    enabled: isSearchTermInitialized,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const { data: companies, isFetching: isCompaniesLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: () =>
      CompanyService.search({
        isAgentIncluded: true,
        isWorkPlacesIncluded: true,
        isAccountManagerIncluded: true,
        isActive: true,
      }).then((data: any) => {
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: systemTypes, isFetching: isSystemTypesLoading } = useQuery({
    queryKey: ['systemTypes'],
    queryFn: () =>
      SystemTypeService.search({
        isDeviceTypeIncluded: true,
        isActive: true,
      }).then((data: any) => {
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const workPlaces = useMemo(() => {
    if (!selectedCompany) return [];
    const company = companies?.find((c: any) => c.name === selectedCompany);
    return company?.workPlaces || [];
  }, [companies, selectedCompany]);

  useEffect(() => {
    if (isSearchTermInitialized && searchTerm === '') {
      refetch();
    }
  }, [searchTerm, isSearchTermInitialized, refetch]);

  const debouncedSearch = useCallback(
    debounce((value: string, search: string) => {
      dispatch(updateProps({ field: search, value: value }));
      dispatch(updateProps({ field: 'skip', value: 0 }));
    }, 1000),
    [],
  );

  const handleSearch = useCallback(
    (value: string) => {
      debouncedSearch(value, 'searchTerm');
    },
    [searchTerm],
  );

  const handleTypeChange = useCallback(
    (value: string) => {
      debouncedSearch(value, 'searchTermType');
    },
    [searchTermType],
  );

  // Add handlers for new filters
  const handleCompanyChange = (value: string | null) => {
    setSelectedCompany(value);
    dispatch(updateProps({ field: 'skip', value: 0 }));
  };

  const handleWorkPlaceChange = (value: string | null) => {
    setSelectedWorkPlace(value);
    dispatch(updateProps({ field: 'skip', value: 0 }));
  };

  const handleSystemTypeChange = (value: string | null) => {
    setSelectedSystemType(value);
    dispatch(updateProps({ field: 'skip', value: 0 }));
  };

  const handleDateChange = (date: Dayjs | null) => {
    setSelectedDate(date);
    dispatch(updateProps({ field: 'skip', value: 0 }));
  };

  const handleModalInformationOpen = (record: any) => {
    setSelectedRecord(record);
    setModalInformation(true);
  };

  const handleModalInformationClose = () => {
    setModalInformation(false);
  };

  // Handlers for selection management
  const handleClearSelection = () => {
    dispatch(clearSelectedRecords());
  };

  const handleBulkDownload = () => {
    if (!selectedRecords.length) {
      message.warning('Vă rugăm să selectați cel puțin o fișă de intervenție pentru descărcare.');
      return;
    }

    selectedRecords.forEach((record: any) => {
      // TODO: Implement download functionality for InterventionFileProject
    });
  };

  // Row selection configuration
  const rowSelection = {
    selectedRowKeys: selectedRecords.map((record: any) => record.id),
    onChange: (_selectedRowKeys: React.Key[], selectedRows: any[]) => {
      // Get current page records
      const currentPageRecords = interventionFiles;

      // Create a map of existing selections
      const existingSelectionsMap = new Map();
      selectedRecords.forEach((record: any) => {
        existingSelectionsMap.set(record.id, record);
      });

      // Update selections for current page
      currentPageRecords.forEach((record: any) => {
        if (selectedRows.some((selected: any) => selected.id === record.id)) {
          existingSelectionsMap.set(record.id, record);
        } else {
          existingSelectionsMap.delete(record.id);
        }
      });

      // Convert map back to array
      const updatedSelections = Array.from(existingSelectionsMap.values());
      dispatch(setSelectedRecords(updatedSelections));
    },
    getCheckboxProps: () => ({
      disabled: false,
    }),
    // Add select all functionality
    selections: [
      {
        key: 'selectAll',
        text: 'Selectează toate',
        onSelect: () => {
          const currentPageRecords = interventionFiles;
          const existingSelectionsMap = new Map();

          // Add existing selections
          selectedRecords.forEach((record: any) => {
            existingSelectionsMap.set(record.id, record);
          });

          // Add all current page records
          currentPageRecords.forEach((record: any) => {
            existingSelectionsMap.set(record.id, record);
          });

          const updatedSelections = Array.from(existingSelectionsMap.values());
          dispatch(setSelectedRecords(updatedSelections));
        },
      },
      {
        key: 'clearAll',
        text: 'Șterge toate selecțiile',
        onSelect: () => {
          dispatch(clearSelectedRecords());
        },
      },
    ],
  };

  const paginationConfig = {
    current: currentPage,
    pageSize: take,
    total: reduxInterventionFileProject.props.totalItems,
    onChange: (page: number, pageSize: number) => {
      const skipValue = (page - 1) * pageSize;
      dispatch(updateProps({ field: 'skip', value: skipValue }));
      dispatch(updateProps({ field: 'take', value: pageSize }));
    },
    showSizeChanger: true,
    pageSizeOptions: pageSizes?.map((size) => size.toString()),
    onShowSizeChange: (_current: number, size: number) => {
      dispatch(updateProps({ field: 'skip', value: 0 }));
      dispatch(updateProps({ field: 'take', value: size }));
    },
    showQuickJumper: false,
  };

  const columns: any[] = useMemo(
    () =>
      [
        {
          title: 'Numar',
          key: 'number',
          render: (_: any, record: any, index: number) => (
            <Tag color={'blue'} style={{ cursor: 'pointer' }}>
              {record?.number ?? skip + index + 1}
            </Tag>
          ),
        },
        {
          title: 'Firma',
          key: 'company',
          render: (_: any, record: any) => {
            const company = record?.pmContract?.company ?? record?.company;
            return (
              <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 200 }}>{company?.name}</div>
            );
          },
        },
        {
          title: 'Punct de lucru',
          dataIndex: 'workPlace',
          key: 'workPlaceName',
          render: (workPlace: any) => (
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 300 }}>
              <b>{workPlace?.name}</b>
            </div>
          ),
        },
        {
          title: 'Adresa',
          dataIndex: 'workPlace',
          key: 'workPlaceAddress',
          render: (workPlace: any) => (
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 300 }}>{workPlace?.address}</div>
          ),
        },
        {
          title: 'Tehnicieni',
          dataIndex: 'interventionFileProjectInstallations',
          key: 'technicians',
          render: (installations: any[]) => (
            <>
              {installations?.length > 0 ? (
                installations?.map((installation: any, index: any) => {
                  return installation.interventionFileProjectInstallationTehnicians?.map(
                    (tech: any, techIndex: any) => <div key={`${index}-${techIndex}`}>{tech?.user?.name}</div>,
                  );
                })
              ) : (
                <div>----</div>
              )}
            </>
          ),
        },
        {
          title: 'Data intervenției',
          dataIndex: 'deliveryDate',
          key: 'deliveryDate',
          render: (deliveryDate: string) => (deliveryDate ? moment(deliveryDate).format('DD-MM-YYYY') : '-'),
        },
        {
          title: 'Durata (ore)',
          dataIndex: 'hours',
          key: 'hours',
          render: (hours: number, record: any) => {
            const totalHours = (hours || 0) + (record?.minutes || 0) / 60;
            return <span>{totalHours.toFixed(1)}h</span>;
          },
        },
        {
          title: 'Status',
          dataIndex: '',
          key: 'isActive',
          render: (record: any) => {
            const isDraft = record?.isDraft === true;
            const isInactive = record?.isActive === false;
            const color = isDraft ? 'orange' : isInactive ? 'red' : 'green';
            const label = isDraft ? 'Draft' : isInactive ? 'Inactiv' : 'Activ';
            return (
              <div>
                <Tag color={color}>{label}</Tag>
              </div>
            );
          },
        },
        {
          title: 'Actiuni',
          dataIndex: '',
          key: 'actions',
          align: 'center',
          render: (record: any) => (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Tooltip title='Vizualizează'>
                <Button
                  style={{ marginRight: 8 }}
                  icon={<EyeOutlined />}
                  onClick={() =>
                    window.open(
                      `/project-management/interventionFileProject/edit?id=${record?.id}&pmContractId=${record?.pmContractId}`,
                      '_blank',
                    )
                  }
                />
              </Tooltip>

              {hasAdminPermission && (
                <Tooltip title='Modifica'>
                  <Button
                    style={{ marginRight: 8 }}
                    icon={<EditOutlined />}
                    onClick={() =>
                      window.open(
                        `/project-management/interventionFileProject/edit?id=${record?.id}&pmContractId=${record?.pmContractId}`,
                        '_blank',
                      )
                    }
                  />
                </Tooltip>
              )}

              <Tooltip title='Informatii'>
                <Button
                  style={{ marginRight: 8 }}
                  icon={<InfoCircleOutlined />}
                  onClick={() => handleModalInformationOpen(record)}
                />
              </Tooltip>

              <Tooltip title='Descarca PDF'>
                <Button icon={<DownloadOutlined />} style={{ marginRight: 8 }} />
              </Tooltip>
            </div>
          ),
        },
      ].filter(Boolean),
    [interventionFiles, selectedRecords, hasAdminPermission, skip],
  );

  if (isInterventionFilesLoading || isCompaniesLoading || isSystemTypesLoading) {
    return <Spin size='large' className='spinner'></Spin>;
  }

  return (
    <>
      <div>
        <h1>Fise de instalare</h1>
        <h2>Perioada : {moment(new Date()).format('YYYY-MM-DD')}</h2>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 20, justifyContent: 'space-between', alignItems: 'center' }}>
        <Col>
          <div className='action-buttons-container'>
            <Tooltip title='Ștergeți toate selecțiile'>
              <Button
                type='default'
                onClick={handleClearSelection}
                disabled={selectedRecords.length === 0}
                icon={<DeleteOutlined />}
                shape='circle'
              />
            </Tooltip>
            <div className='bulk-download-button'>
              <Tooltip title={`Descarca fișele de intervenție selectate (${selectedRecords.length})`}>
                <Button
                  type='primary'
                  onClick={handleBulkDownload}
                  disabled={selectedRecords.length === 0}
                  icon={<DownloadOutlined />}
                  shape='circle'
                />
              </Tooltip>
              {selectedRecords.length > 0 && (
                <Badge count={selectedRecords.length} color='blue' className='bulk-download-badge' />
              )}
            </div>
          </div>
        </Col>
        <Col>
          <Tooltip title='Creeaza fisa de instalare' trigger={'hover'} placement='left'>
            <Button
              type='primary'
              onClick={() => window.open(`/project-management/interventionFileProject/new`, '_blank')}
              icon={<PlusOutlined />}
              shape='circle'
            />
          </Tooltip>
        </Col>
      </Row>

      <InterventionFileProjectInformation
        handleModalInformationClose={handleModalInformationClose}
        modalInformation={modalInformation}
        selectedRecord={selectedRecord}
      />

      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        <Col xs={24} sm={12} md={8}>
          <Input.Search
            placeholder='Search...'
            defaultValue={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            autoFocus={true}
            allowClear
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Select
            placeholder='Filtreaza dupa tip'
            value={searchTermType || null}
            onChange={handleTypeChange}
            style={{ width: '100%' }}
            allowClear
          >
            <Select.Option value='Service [cu contract]'>Service [cu contract]</Select.Option>
            <Select.Option value='Service [fara contract]'>Service [fara contract]</Select.Option>
            <Select.Option value='Garantie'>Garantie</Select.Option>
            <Select.Option value='Mentenanta periodica'>Mentenanta periodica</Select.Option>
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <DatePicker
            placeholder='Filtreaza dupa data'
            value={selectedDate}
            onChange={handleDateChange}
            style={{ width: '100%' }}
            format='DD-MM-YYYY'
            allowClear
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        <Col xs={24} sm={12} md={8}>
          <Select
            placeholder='Filtreaza dupa beneficiar'
            value={selectedCompany}
            onChange={handleCompanyChange}
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp='children'
          >
            {companies?.map((company: any) => (
              <Select.Option key={company?.id} value={company?.name}>
                {company?.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Select
            placeholder='Filtreaza dupa punct de lucru'
            value={selectedWorkPlace}
            onChange={handleWorkPlaceChange}
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp='children'
          >
            {workPlaces?.map((workPlace: any) => (
              <Select.Option key={workPlace?.id} value={workPlace?.name}>
                {workPlace?.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Select
            placeholder='Filtreaza dupa sistem'
            value={selectedSystemType}
            onChange={handleSystemTypeChange}
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp='children'
          >
            {systemTypes?.map((systemType: any) => (
              <Select.Option key={systemType?.id} value={systemType?.name}>
                {systemType?.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
      </Row>

      <Table
        dataSource={interventionFiles}
        columns={columns}
        rowKey='id'
        pagination={paginationConfig}
        scroll={{ x: 'max-content' }}
        style={{ boxShadow: '0px 0px 10px rgba(0,0,0,0.2)' }}
        rowClassName={(record: any) => {
          return record?.interventionFilePricing?.isFree == false ? 'error-row' : '';
        }}
        rowSelection={rowSelection}
      />
    </>
  );
};

export default InterventionFileProjectTable;
